// A smooth, non-voxel surface for a mass of voxels (hi-fi units).
//
// The voxels of a part are blurred into a density field and meshed with surface
// nets (one vertex per straddling cell, quads across sign changes), so a torso
// or a robe reads as one sculpted shape rather than a stack of cubes. Every
// vertex takes its colour, roughness, metalness, glow and team flag from the
// nearest voxel, so the attribute set matches `boxForVoxel` and the part merges
// with the rest of the unit.
//
// Voxel centres sit on integer lattice points (`boxForVoxel` translates a
// centred box to `v * VOXEL_SCALE`). A lone voxel keeps a small rounded blob,
// so eyes, studs and buttons survive the smoothing.
import type { BlockVoxel } from '@aod/ui/prefabs';
import { VOXEL_SCALE, materialClassOf } from '@aod/ui/prefabs';
import * as THREE from 'three';

/** Where the blurred density crosses into "solid". */
const ISO = 0.5;
/** A voxel's own weight, so a lone voxel is never blurred away. */
const SOLID = 0.9;
/** The blurred surface lands about a voxel outside a face's centre; this pulls it back toward the voxel box (voxels, along the normal). */
const GROW = -0.12;
/** Below this many voxels a part is left to the rounded-box path. */
export const SMOOTH_MIN_VOXELS = 4;

interface Lattice {
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  readonly f: Float32Array;
}

const at = (l: Lattice, x: number, y: number, z: number) => x + l.nx * (y + l.ny * z);

function buildField(voxels: readonly BlockVoxel[]): Lattice {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of voxels) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x);
    maxY = Math.max(maxY, v.y);
    maxZ = Math.max(maxZ, v.z);
  }
  // Two cells of padding: the blur reaches one, the surface one more.
  const pad = 2;
  const ox = minX - pad;
  const oy = minY - pad;
  const oz = minZ - pad;
  const l = {
    nx: maxX - minX + 1 + pad * 2,
    ny: maxY - minY + 1 + pad * 2,
    nz: maxZ - minZ + 1 + pad * 2,
    ox,
    oy,
    oz,
  };
  const size = l.nx * l.ny * l.nz;
  const occ = new Float32Array(size);
  for (const v of voxels) occ[at({ ...l, f: occ }, v.x - ox, v.y - oy, v.z - oz)] = 1;
  // Separable [1 2 1]/4 blur on each axis.
  const blur = (src: Float32Array, axis: 0 | 1 | 2): Float32Array => {
    const out = new Float32Array(size);
    const step = axis === 0 ? 1 : axis === 1 ? l.nx : l.nx * l.ny;
    const len = axis === 0 ? l.nx : axis === 1 ? l.ny : l.nz;
    for (let z = 0; z < l.nz; z++) {
      for (let y = 0; y < l.ny; y++) {
        for (let x = 0; x < l.nx; x++) {
          const i = x + l.nx * (y + l.ny * z);
          const c = axis === 0 ? x : axis === 1 ? y : z;
          const lo = c > 0 ? (src[i - step] ?? 0) : 0;
          const hi = c < len - 1 ? (src[i + step] ?? 0) : 0;
          out[i] = 0.25 * lo + 0.5 * (src[i] ?? 0) + 0.25 * hi;
        }
      }
    }
    return out;
  };
  const blurred = blur(blur(blur(occ, 0), 1), 2);
  const f = new Float32Array(size);
  for (let i = 0; i < size; i++) f[i] = Math.max(blurred[i] ?? 0, (occ[i] ?? 0) * SOLID);
  return { ...l, f };
}

// The 12 edges of a cell as pairs of corner indices (corner = x | y<<1 | z<<2).
const EDGES: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

/**
 * A smooth indexed surface of `voxels` in lattice units, plus the lattice for
 * colour lookups. Returns null when nothing crosses the iso level.
 */
function mesh(voxels: readonly BlockVoxel[]): { geo: THREE.BufferGeometry; l: Lattice } | null {
  const l = buildField(voxels);
  const { nx, ny, nz, f } = l;
  const cx = nx - 1;
  const cy = ny - 1;
  const cz = nz - 1;
  const cellIndex = new Int32Array(cx * cy * cz).fill(-1);
  const positions: number[] = [];

  for (let z = 0; z < cz; z++) {
    for (let y = 0; y < cy; y++) {
      for (let x = 0; x < cx; x++) {
        const val: number[] = [];
        for (let c = 0; c < 8; c++) {
          val.push(f[at(l, x + (c & 1), y + ((c >> 1) & 1), z + ((c >> 2) & 1))] ?? 0);
        }
        let sx = 0;
        let sy = 0;
        let sz = 0;
        let n = 0;
        for (const [a, b] of EDGES) {
          const va = val[a] ?? 0;
          const vb = val[b] ?? 0;
          if (va > ISO === vb > ISO) continue;
          const t = (ISO - va) / (vb - va);
          sx += x + (a & 1) + t * ((b & 1) - (a & 1));
          sy += y + ((a >> 1) & 1) + t * (((b >> 1) & 1) - ((a >> 1) & 1));
          sz += z + ((a >> 2) & 1) + t * (((b >> 2) & 1) - ((a >> 2) & 1));
          n++;
        }
        if (n === 0) continue;
        cellIndex[x + cx * (y + cy * z)] = positions.length / 3;
        positions.push(sx / n, sy / n, sz / n);
      }
    }
  }
  if (positions.length === 0) return null;

  const indices: number[] = [];
  const cell = (x: number, y: number, z: number) =>
    x < 0 || y < 0 || z < 0 || x >= cx || y >= cy || z >= cz
      ? -1
      : (cellIndex[x + cx * (y + cy * z)] ?? -1);
  const quad = (a: number, b: number, c: number, d: number, flip: boolean) => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    if (flip) indices.push(a, c, b, a, d, c);
    else indices.push(a, b, c, a, c, d);
  };
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        const here = (f[at(l, x, y, z)] ?? 0) > ISO;
        if (x + 1 < nx && here !== (f[at(l, x + 1, y, z)] ?? 0) > ISO) {
          quad(cell(x, y - 1, z - 1), cell(x, y, z - 1), cell(x, y, z), cell(x, y - 1, z), !here);
        }
        if (y + 1 < ny && here !== (f[at(l, x, y + 1, z)] ?? 0) > ISO) {
          quad(cell(x - 1, y, z - 1), cell(x - 1, y, z), cell(x, y, z), cell(x, y, z - 1), !here);
        }
        if (z + 1 < nz && here !== (f[at(l, x, y, z + 1)] ?? 0) > ISO) {
          quad(cell(x - 1, y - 1, z), cell(x, y - 1, z), cell(x, y, z), cell(x - 1, y, z), !here);
        }
      }
    }
  }
  if (indices.length === 0) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  return { geo, l };
}

/** The voxel nearest a lattice-space point, searched a cell around. */
function nearest(
  byPos: Map<string, BlockVoxel>,
  x: number,
  y: number,
  z: number,
): BlockVoxel | undefined {
  const rx = Math.round(x);
  const ry = Math.round(y);
  const rz = Math.round(z);
  let best: BlockVoxel | undefined;
  let bestD = Number.POSITIVE_INFINITY;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const v = byPos.get(`${rx + dx},${ry + dy},${rz + dz}`);
        if (!v) continue;
        const d = (v.x - x) ** 2 + (v.y - y) ** 2 + (v.z - z) ** 2;
        if (d < bestD) {
          bestD = d;
          best = v;
        }
      }
    }
  }
  return best;
}

/**
 * The smooth surface of a part as a non-indexed geometry in world units, with
 * the per-vertex attributes `boxForVoxel` writes. Null when the part is too
 * small to smooth (the caller then falls back to rounded boxes).
 */
export function smoothPartGeometry(
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
  defaults: { readonly roughness: number; readonly metalness: number },
): THREE.BufferGeometry | null {
  if (voxels.length < SMOOTH_MIN_VOXELS) return null;
  const built = mesh(voxels);
  if (!built) return null;
  const { geo, l } = built;

  // Back from lattice cells to unit space, pushed out along the smooth normal.
  geo.computeVertexNormals();
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const nrm = geo.getAttribute('normal') as THREE.BufferAttribute;
  const byPos = new Map<string, BlockVoxel>();
  for (const v of voxels) byPos.set(`${v.x},${v.y},${v.z}`, v);
  const count = pos.count;
  const colors = new Float32Array(count * 3);
  const roughness = new Float32Array(count);
  const metalness = new Float32Array(count);
  const emissive = new Float32Array(count * 3);
  const team = new Float32Array(count);
  const mat = new Float32Array(count);
  const c = new THREE.Color();
  const e = new THREE.Color();
  for (let i = 0; i < count; i++) {
    const lx = pos.getX(i) + l.ox;
    const ly = pos.getY(i) + l.oy;
    const lz = pos.getZ(i) + l.oz;
    const v = nearest(byPos, lx, ly, lz) ?? voxels[0];
    if (!v) continue;
    c.set(v.color);
    e.set(v.emissive ?? '#000000');
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
    roughness[i] = v.roughness ?? defaults.roughness;
    metalness[i] = v.metalness ?? defaults.metalness;
    emissive[i * 3] = e.r;
    emissive[i * 3 + 1] = e.g;
    emissive[i * 3 + 2] = e.b;
    team[i] = legacyTint ? -1 : v.team ? 1 : 0;
    mat[i] = materialClassOf(v);
    pos.setXYZ(
      i,
      (lx + nrm.getX(i) * GROW) * VOXEL_SCALE,
      (ly + nrm.getY(i) * GROW) * VOXEL_SCALE,
      (lz + nrm.getZ(i) * GROW) * VOXEL_SCALE,
    );
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aRoughness', new THREE.BufferAttribute(roughness, 1));
  geo.setAttribute('aMetalness', new THREE.BufferAttribute(metalness, 1));
  geo.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
  geo.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
  geo.setAttribute('aMat', new THREE.BufferAttribute(mat, 1));
  // The box and primitive parts carry uvs; a merge needs every part to agree.
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(count * 2), 2));
  geo.computeVertexNormals();
  // Merge-compatible with the box and primitive parts, which are non-indexed.
  return geo.toNonIndexed();
}
